import { ArrowDownToLine, BookmarkCheck, ChevronDown, Coins, LineChart, Loader2, Save, Send, Zap } from 'lucide-react';
import { useCallback, useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { adminWithdraw, fetchAdminProfit } from '@/api/admin';
import { fetchMe, setWallets } from '@/api/member';
import { fmtUsd } from '@/lib/money';

const RANGES = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'all', label: 'All-Time' },
];

const money = (v) => `$${Number(v ?? 0).toFixed(2)}`;

const ProfitSubpage = () => {
  const [range, setRange] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Withdrawal module
  const [amount, setAmount] = useState('');
  const [asset, setAsset] = useState('LTC');
  const [destination, setDestination] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  // Saved-address mirror of the admin's personal wallet_addresses. The
  // admin profit withdraw backend already falls back to admin.wallet_addresses
  // when the destination_address field is omitted, so saving here is just
  // a frontend convenience that calls PUT /api/me/wallets like the member
  // withdraw panel does.
  const [me, setMe] = useState(null);
  const [saveToProfile, setSaveToProfile] = useState(true);
  const [savingAddress, setSavingAddress] = useState(false);
  const uid = useId();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await fetchAdminProfit(range);
      setData(d);
    } catch {
      toast.error('Failed to load profit ledger');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  // Bootstrap saved admin wallet addresses (called once + after a save).
  const refreshMe = useCallback(async () => {
    try {
      const m = await fetchMe();
      setMe(m);
    } catch {
      // Non-fatal \u2014 the withdraw module still works without the pre-fill.
    }
  }, []);

  useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  // Clear the destination override + reset save toggle when switching assets
  // so the user never carries a typo from one chain into another.
  // biome-ignore lint/correctness/useExhaustiveDependencies: asset is the intentional trigger — reset the override whenever the selected chain changes
  useEffect(() => {
    setDestination('');
    setSaveToProfile(true);
  }, [asset]);

  const savedAddress = me?.wallet_addresses?.[asset] || '';
  const typedAddress = destination.trim();
  // Treat blank input as "use saved" \u2014 same fallback the backend does.
  const effectiveAddress = (typedAddress || savedAddress).trim();
  const isNewAddress = typedAddress.length > 0 && typedAddress !== savedAddress;

  /**
   * Persist the typed admin wallet address to the user's own profile via
   * the existing PUT /api/me/wallets endpoint (the admin IS a user). The
   * backend admin-withdraw flow reads from admin.wallet_addresses so the
   * next withdraw uses this saved value automatically.
   */
  const persistAddress = async () => {
    if (!isNewAddress) return true;
    setSavingAddress(true);
    try {
      const updated = await setWallets({ [asset]: typedAddress });
      setMe(updated);
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Couldn't save address: ${msg}`);
      return false;
    } finally {
      setSavingAddress(false);
    }
  };

  const handleSaveAddressOnly = async () => {
    if (!isNewAddress || savingAddress) return;
    const ok = await persistAddress();
    if (ok) {
      toast.success(`${asset} admin wallet saved \u2014 will pre-fill on future withdrawals.`);
      setDestination('');
    }
  };

  const submit = async () => {
    setWithdrawing(true);
    try {
      // If the user typed a new address AND opted to save, persist first
      // so it's available for the backend's wallet_addresses fallback in
      // the same call (the backend would also use destination_address
      // directly if we pass it, but saving first means future withdrawals
      // auto-pre-fill correctly even if the user clears the input).
      if (isNewAddress && saveToProfile) {
        await persistAddress();
      }
      const r = await adminWithdraw({
        amount: String(amount).trim(),
        asset,
        destination_address: effectiveAddress || undefined,
      });
      toast.success(`Withdrawal queued (${fmtUsd(r.amount_usd)} USD in ${r.asset})`);
      setAmount('');
      setDestination('');
      load();
    } catch (err) {
      // Extract human-readable message from BOTH error envelopes the
      // backend may produce:
      //   - FastAPI default:           {"detail": "..."}  or  {"detail": {"code","message"}}
      //   - Security middleware (503): {"error":  {"code","message"}}
      // Without checking BOTH, security middleware 503s (event-loop busy,
      // conn-cap, UAM gate, request timeout) would fall through to the
      // generic axios "Request failed with status code 503" message.
      const data = err?.response?.data;
      const detail = data?.detail;
      const mwError = data?.error;
      let msg;
      if (typeof detail === 'object' && detail !== null) {
        msg = detail?.message || detail?.code;
      } else if (typeof detail === 'string') {
        msg = detail;
      } else if (mwError && typeof mwError === 'object') {
        msg = mwError?.message || mwError?.code;
      } else {
        msg = err.message;
      }
      toast.error(`Withdraw failed: ${msg}`);
    } finally {
      setWithdrawing(false);
    }
  };

  const rangeLabel = RANGES.find((r) => r.value === range)?.label || 'All-Time';

  return (
    <div className="space-y-6" data-testid="profit-subpage">
      <div className="sc-glass rounded-2xl p-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="font-display font-bold text-xl flex items-center gap-2">
            <LineChart className="w-5 h-5 text-[#FFC20E]" />
            Core Profit Ledger Matrix
          </div>
          <p className="text-xs text-[var(--sc-text-dim)] mt-1">
            Cumulative platform gains. Cycle Fees — the $0.25 charged on every completed matrix. Orphan Matches — $0.25 sponsor share diverted from sponsorless
            or globally expired profiles.
          </p>
        </div>
        <div className="relative">
          <button
            type="button"
            onClick={() => setDropdownOpen((v) => !v)}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[rgba(255,182,18,0.32)] bg-[rgba(255,182,18,0.04)] text-[11px] uppercase tracking-[0.18em] font-semibold text-[#FFC20E]"
            data-testid="profit-range-btn"
          >
            {rangeLabel}
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          {dropdownOpen && (
            <>
              <button type="button" aria-label="Close" tabIndex={-1} className="fixed inset-0 z-[35]" onClick={() => setDropdownOpen(false)} />
              <div className="absolute right-0 mt-2 z-[40] sc-glass-strong rounded-xl overflow-hidden w-[160px]">
                {RANGES.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => {
                      setRange(r.value);
                      setDropdownOpen(false);
                    }}
                    className={`block w-full text-left px-3 py-2 text-[12px] transition ${
                      range === r.value
                        ? 'bg-[rgba(255,182,18,0.10)] text-[#FFC20E] font-semibold'
                        : 'text-[var(--sc-text-dim)] hover:bg-[rgba(255,182,18,0.04)]'
                    }`}
                    data-testid={`profit-range-${r.value}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="sc-glass rounded-2xl p-5 md:p-6 relative overflow-hidden" data-testid="profit-card-cycle">
          <div
            className="absolute -top-14 -right-14 w-40 h-40 pointer-events-none rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(255,182,18,0.18), transparent 60%)' }}
          />
          <div className="relative z-10">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center border"
              style={{ background: 'rgba(255,182,18,0.06)', borderColor: 'rgba(255,182,18,0.28)' }}
            >
              <Coins className="w-5 h-5 text-[#FFC20E]" />
            </div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mt-4">Cycle Fees Accumulated</div>
            <div
              className="sc-tabular font-display font-bold text-3xl md:text-4xl mt-2 leading-none sc-gold-text flex items-center gap-2"
              data-testid="profit-cycle-value"
            >
              {money(data?.cycle_fees_usd)}
              {loading && <Loader2 className="w-4 h-4 animate-spin text-[#FFB612]" />}
            </div>
            <div className="text-[12px] text-[var(--sc-text-dim)] mt-2">
              {data?.cycle_count ?? 0} fee events captured in {rangeLabel.toLowerCase()} window.
            </div>
          </div>
        </div>

        <div className="sc-glass rounded-2xl p-5 md:p-6 relative overflow-hidden" data-testid="profit-card-orphan">
          <div
            className="absolute -top-14 -right-14 w-40 h-40 pointer-events-none rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(255,194,14,0.18), transparent 60%)' }}
          />
          <div className="relative z-10">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center border"
              style={{ background: 'rgba(255,182,18,0.06)', borderColor: 'rgba(255,182,18,0.28)' }}
            >
              <Zap className="w-5 h-5 text-[#FFC20E]" />
            </div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mt-4">Orphan Referral Matches</div>
            <div
              className="sc-tabular font-display font-bold text-3xl md:text-4xl mt-2 leading-none sc-gold-text flex items-center gap-2"
              data-testid="profit-orphan-value"
            >
              {money(data?.orphan_matches_usd)}
              {loading && <Loader2 className="w-4 h-4 animate-spin text-[#FFB612]" />}
            </div>
            <div className="text-[12px] text-[var(--sc-text-dim)] mt-2">
              {data?.orphan_count ?? 0} orphan-diverted matches in {rangeLabel.toLowerCase()} window.
            </div>
          </div>
        </div>

        <div className="sc-glass-strong rounded-2xl p-5 md:p-6 relative overflow-hidden" data-testid="profit-card-total">
          <div
            className="absolute -top-14 -right-14 w-40 h-40 pointer-events-none rounded-full"
            style={{ background: 'radial-gradient(circle, rgba(255,182,18,0.28), transparent 60%)' }}
          />
          <div className="relative z-10">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#FFC20E]">Total ({rangeLabel})</div>
            <div
              className="sc-tabular font-display font-black text-4xl md:text-5xl mt-2 leading-none"
              style={{ color: '#FFE08A' }}
              data-testid="profit-total-value"
            >
              {money(data?.total_usd)}
            </div>
            <div className="text-[12px] text-[var(--sc-text-dim)] mt-3">Live admin pool balance (cumulative, all-time):</div>
            <div className="sc-tabular text-[#FFC20E] text-xl font-bold" data-testid="profit-pool-balance">
              {money(data?.admin_pool_balance_usd)}
            </div>
          </div>
        </div>
      </div>

      <div className="sc-glass rounded-2xl p-5 md:p-6" data-testid="admin-withdraw-module">
        <div className="flex items-center gap-2">
          <ArrowDownToLine className="w-4 h-4 text-[#FFC20E]" />
          <div className="font-display font-semibold text-lg">Admin Withdrawal Request</div>
        </div>
        <p className="text-xs text-[var(--sc-text-dim)] mt-1">
          Request capital distribution from the admin pool to a secure external admin wallet. Pending requests will appear in the NOWPayments Mass Payouts
          panel.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          <div>
            <label htmlFor={`${uid}-amount`} className="block text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5">
              Amount (USD)
            </label>
            <input
              id={`${uid}-amount`}
              className="sc-input"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="e.g. 25.00"
              data-testid="admin-withdraw-amount"
            />
          </div>
          <div>
            <span className="block text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5">Asset</span>
            <div className="grid grid-cols-3 gap-1.5">
              {['LTC', 'TRX', 'DOGE'].map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAsset(a)}
                  className={`px-2 py-2 rounded-lg text-[12px] font-semibold border transition ${
                    asset === a
                      ? 'bg-[rgba(255,182,18,0.12)] border-[rgba(255,182,18,0.55)] text-[#FFC20E]'
                      : 'bg-transparent border-[rgba(255,182,18,0.18)] text-[var(--sc-text-dim)] hover:border-[rgba(255,182,18,0.32)]'
                  }`}
                  data-testid={`admin-withdraw-asset-${a}`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label htmlFor={`${uid}-dest`} className="block text-[11px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] mb-1.5">
              Destination Address {savedAddress ? '(saved profile used if blank)' : '(optional — falls back to profile)'}
            </label>
            <input
              id={`${uid}-dest`}
              className="sc-input"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder={savedAddress ? `${savedAddress.slice(0, 12)}\u2026 (saved \u2014 leave blank to reuse)` : `${asset} address`}
              data-testid="admin-withdraw-dest"
              autoComplete="off"
              spellCheck="false"
            />
          </div>
        </div>

        {/* Save-to-profile row \u2014 only when the typed address differs from
            what's saved on the admin's own wallet_addresses for this asset.
            Mirrors the LiveWithdrawPanel pattern so admin and member share
            the same UX vocabulary. */}
        {isNewAddress && (
          <div
            className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border px-3 py-2"
            style={{
              background: 'rgba(255,182,18,0.06)',
              borderColor: 'rgba(255,182,18,0.28)',
            }}
            data-testid="admin-withdraw-save-row"
          >
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={saveToProfile}
                onChange={(e) => setSaveToProfile(e.target.checked)}
                className="w-4 h-4 accent-[#FFC20E] cursor-pointer"
                data-testid="admin-withdraw-save-checkbox"
              />
              <span className="text-[12px] text-[var(--sc-text)] leading-snug">
                <BookmarkCheck className="w-3.5 h-3.5 inline-block mr-1 text-[#FFC20E] -mt-0.5" />
                Save this {asset} admin wallet address for future withdrawals
                {savedAddress ? (
                  <span className="block text-[10px] text-[var(--sc-text-mute)] mt-0.5">Replaces saved: {savedAddress.slice(0, 14)}…</span>
                ) : (
                  <span className="block text-[10px] text-[var(--sc-text-mute)] mt-0.5">No saved {asset} wallet yet — it'll be added.</span>
                )}
              </span>
            </label>
            <button
              type="button"
              onClick={handleSaveAddressOnly}
              disabled={savingAddress}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] uppercase tracking-[0.16em] font-bold transition disabled:opacity-60 shrink-0"
              style={{
                background: 'rgba(0,0,0,0.42)',
                border: '1px solid rgba(255,182,18,0.55)',
                color: '#FFE08A',
              }}
              data-testid="admin-withdraw-save-btn"
              title="Save without submitting a withdrawal"
            >
              {savingAddress ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save address
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={withdrawing || !amount}
          className="sc-btn-gold mt-4"
          style={{ padding: '12px 22px', fontSize: 12 }}
          data-testid="admin-withdraw-submit"
        >
          {withdrawing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Queue Admin Withdrawal
        </button>
      </div>
    </div>
  );
};

export default ProfitSubpage;
